<template>
  <modal :name="modalName" width="94%" height="auto" :max-width="960" :max-height="900"
    :adaptive="true" :scrollable="true" :click-to-close="false" @opened="focusClose" @before-close="beforeClose">
    <section ref="dialog" class="insurance-client-modal" role="dialog" aria-modal="true"
      aria-labelledby="insurance-client-modal-title" @keydown.tab="trapFocus" @keyup.esc.stop.prevent="close">
      <AppModalHeader ref="header" design="new" eyebrow="Insurance updates"
        :title="detail ? detail.client.client_name : 'Client details'" title-id="insurance-client-modal-title" @close="close" />
      <div class="insurance-client-modal__body">
        <div v-if="loading" class="insurance-client-modal__state">Loading client details…</div>
        <div v-else-if="error" class="insurance-client-modal__state insurance-client-modal__error" role="alert">
          {{ error }} <AppButton design="new" theme="dark" size="small" @click.native="load">Retry</AppButton>
        </div>
        <template v-else-if="detail">
          <div class="insurance-client-modal__summary">
            <div><span>Client</span><router-link :to="{ name: 'clients-detail', params: { id: detail.client.id } }">{{ detail.client.client_name }}</router-link></div>
            <div><span>Group</span><router-link v-if="detail.client.group" :to="{ name: 'clients-groups-general', params: { id: detail.client.group.id } }">{{ detail.client.group.name }}</router-link><strong v-else>—</strong></div>
            <div><span>Arrival</span><strong>{{ dateOnly(detail.client.arrival_date) }} · {{ daysLabel(detail.client.days_to_arrival) }}</strong></div>
            <div><span>Language</span><strong>{{ languageLabel(detail.client.language, detail.client.language_source) }}</strong></div>
            <div><span>Email address</span><strong :title="detail.client.email">{{ detail.client.email || '—' }}</strong></div>
            <div><span>Phone</span><strong>{{ detail.client.phone || '—' }}</strong></div>
          </div>
          <section class="insurance-client-modal__cards">
            <article v-for="channel in channels" :key="channel.value" class="insurance-client-modal__card">
              <div class="insurance-client-modal__card-heading">
                <h3>{{ channel.label }}</h3>
                <AppStatusBadge design="new" compact :tone="statusTone(delivery(channel.value).status)" :label="statusLabel(delivery(channel.value).status)" />
              </div>
              <dl>
                <div><dt>Why</dt><dd>{{ reasonLabel(delivery(channel.value).reason) }}</dd></div>
                <div><dt>Sent to</dt><dd>{{ delivery(channel.value).recipient || '—' }}</dd></div>
                <div><dt>Message template</dt><dd>{{ delivery(channel.value).template_code || '—' }}</dd></div>
                <div><dt>Language</dt><dd>{{ delivery(channel.value).language ? delivery(channel.value).language.toUpperCase() : '—' }}<AppStatusBadge v-if="delivery(channel.value).language_fallback" design="new" compact tone="amber" label="In English" /></dd></div>
                <div><dt>Last checked</dt><dd>{{ dateTime(delivery(channel.value).last_checked_at) }}</dd></div>
                <div v-if="delivery(channel.value).operation_updated_at"><dt>Last delivery update</dt><dd>{{ dateTime(delivery(channel.value).operation_updated_at) }}</dd></div>
              </dl>
              <div v-if="canEdit && delivery(channel.value).available_action" class="insurance-client-modal__actions">
                <template v-if="confirmChannel === channel.value">
                  <span>{{ confirmText(channel) }}</span>
                  <AppButton design="new" theme="dark" size="small" :disabled="Boolean(workingChannel)" @click.native="confirmChannel = ''">Cancel</AppButton>
                  <AppButton design="new" :theme="delivery(channel.value).available_action === 'retry' ? 'warn' : 'success'" size="small" :disabled="Boolean(workingChannel)" @click.native="dispatch(channel.value)">{{ workingChannel === channel.value ? 'Sending…' : 'Yes, send' }}</AppButton>
                </template>
                <AppButton v-else design="new" :theme="delivery(channel.value).available_action === 'retry' ? 'warn' : 'success'" size="small" :disabled="Boolean(workingChannel)" @click.native="confirmChannel = channel.value">{{ actionLabel(channel) }}</AppButton>
              </div>
              <p v-if="actionError[channel.value]" class="insurance-client-modal__action-error" role="alert">{{ actionError[channel.value] }}</p>
              <router-link v-if="canOpenOperations && delivery(channel.value).operation_id" :to="operationRoute(delivery(channel.value))">Open in Message Operations</router-link>
              <div v-if="delivery(channel.value).attempts && delivery(channel.value).attempts.length" class="insurance-client-modal__attempts">
                <h4>Sending history</h4>
                <div v-for="attempt in delivery(channel.value).attempts" :key="attempt.number" class="insurance-client-modal__attempt">
                  <span>#{{ attempt.number }} · {{ attemptLabel(attempt.trigger) }} · {{ dateTime(attempt.created_at) }} · {{ statusLabel(attempt.status) }}</span>
                  <span v-if="attempt.reason">{{ reasonLabel(attempt.reason) }}</span>
                  <router-link v-if="canOpenOperations && attempt.operation_id" :to="operationRoute({ channel: channel.value, operation_id: attempt.operation_id })">Message Operations</router-link>
                </div>
              </div>
            </article>
          </section>
        </template>
      </div>
      <footer class="insurance-client-modal__footer"><AppButton design="new" theme="dark" @click.native="close">Close</AppButton></footer>
    </section>
  </modal>
</template>

<script>
import { getGroundOpsInsuranceUpdateClient, postGroundOpsInsuranceUpdateChannel } from '@/http'
import { groundOpsKey } from '@/utils/groundOpsAccess'
import { techSupportKey } from '@/utils/techSupportAccess'
import { dateOnly, dateTime, daysLabel, languageLabel, operationRoute, reasonLabel, statusLabel, statusTone } from './model'

export default {
  name: 'InsuranceUpdateClientDetailModal',
  props: { clientId: { type: Number, required: true } },
  data() {
    return {
      modalName: 'insurance-update-client-detail',
      detail: null,
      loading: false,
      error: '',
      actionError: {},
      confirmChannel: '',
      workingChannel: '',
      requestNumber: 0,
      channels: [{ value: 'email', label: 'Email' }, { value: 'whatsapp', label: 'WhatsApp' }]
    }
  },
  computed: {
    canEdit() { return this.$store.getters['permissions/can'](groundOpsKey('insurance_updates', 'edit')) },
    canOpenOperations() { return this.$store.getters['permissions/can'](techSupportKey('message_operations')) }
  },
  mounted() { this.$modal.show(this.modalName); this.load() },
  beforeDestroy() { this.requestNumber += 1 },
  methods: {
    dateOnly,
    dateTime,
    daysLabel,
    languageLabel,
    operationRoute,
    reasonLabel,
    statusLabel,
    statusTone,
    attemptLabel(trigger) { return { campaign: 'Automatic run', manual_send: 'Sent by hand', manual_retry: 'Sent again by hand' }[trigger] || trigger },
    actionLabel(channel) {
      const retry = this.delivery(channel.value).available_action === 'retry'
      return channel.value === 'email' ? (retry ? 'Send email again' : 'Send email') : (retry ? 'Send WhatsApp again' : 'Send WhatsApp message')
    },
    confirmText(channel) {
      const retry = this.delivery(channel.value).available_action === 'retry'
      const what = channel.value === 'email' ? 'an email' : 'a WhatsApp message'
      return retry ? `The first attempt failed. Send ${what} to the client again?` : `Send ${what} to the client now? They will receive it.`
    },
    focusClose() { this.$refs.header.focusClose() },
    close() { if (!this.workingChannel) this.$modal.hide(this.modalName) },
    beforeClose(event) { if (this.workingChannel) event.cancel(); else this.$emit('close') },
    delivery(channel) { return this.detail.deliveries.find(item => item.channel === channel) || { status: 'not_prepared' } },
    async load() {
      const requestNumber = ++this.requestNumber
      this.loading = true
      this.error = ''
      try {
        const response = await getGroundOpsInsuranceUpdateClient(this.clientId)
        if (requestNumber === this.requestNumber) this.detail = response.data
      } catch (error) {
        if (requestNumber === this.requestNumber) this.error = 'Could not load client details.'
      } finally {
        if (requestNumber === this.requestNumber) this.loading = false
      }
    },
    async dispatch(channel) {
      if (!this.canEdit || this.workingChannel || this.confirmChannel !== channel) return
      const action = this.delivery(channel).available_action
      if (!action) return
      this.workingChannel = channel
      this.$set(this.actionError, channel, '')
      try {
        await postGroundOpsInsuranceUpdateChannel(this.clientId, channel, action)
        this.confirmChannel = ''
        await this.load()
        this.$emit('updated')
      } catch (error) {
        const details = error.response && error.response.data && error.response.data.details
        this.$set(this.actionError, channel, typeof details === 'string' ? details : 'Could not send this update. Check the delivery status before trying again.')
        await this.load()
      } finally {
        this.workingChannel = ''
      }
    },
    trapFocus(event) {
      const elements = Array.from(this.$refs.dialog.querySelectorAll('button:not([disabled]),a[href],input:not([disabled]),[tabindex="0"]')).filter(element => element.getClientRects().length)
      if (!elements.length) return
      const first = elements[0]
      const last = elements[elements.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() } else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
  }
}
</script>

<style scoped lang="scss">
.insurance-client-modal { color: $ui-text; background: $ui-surface; font-family: $ui-font-body; }
.insurance-client-modal__body { display: grid; gap: $ui-space-24; padding: $ui-space-24; }
.insurance-client-modal__state { padding: $ui-space-24; color: $ui-muted; }
.insurance-client-modal__error { display: flex; justify-content: space-between; align-items: center; color: $ui-red; }
.insurance-client-modal__summary { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: $ui-space-16; }
.insurance-client-modal__summary > div { min-width: 0; padding: $ui-space-16; border: 1px solid $ui-line; border-radius: $ui-control-radius; }
.insurance-client-modal__summary span, .insurance-client-modal__card dt { display: block; margin-bottom: $ui-space-4; color: $ui-muted; font-size: $ui-font-size-metadata; }
.insurance-client-modal__summary strong, .insurance-client-modal__summary a { display: block; overflow: hidden; color: $ui-ink; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
.insurance-client-modal a { color: $ui-blue; text-decoration: none; }
.insurance-client-modal a:hover { text-decoration: underline; }
.insurance-client-modal h3 { margin: 0 0 $ui-space-12; color: $ui-ink; font-family: $ui-font-heading; font-size: $ui-font-size-section-title; }
.insurance-client-modal__card { padding: $ui-space-20; border: 1px solid $ui-line; border-radius: $ui-control-radius; }
.insurance-client-modal__cards { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: $ui-space-16; }
.insurance-client-modal__card-heading { display: flex; justify-content: space-between; align-items: center; }
.insurance-client-modal__card-heading h3 { margin: 0; }
.insurance-client-modal__card dl { display: grid; gap: $ui-space-12; margin: $ui-space-16 0; }
.insurance-client-modal__card dl div { min-width: 0; }
.insurance-client-modal__card dd { margin: 0; overflow-wrap: anywhere; }
.insurance-client-modal__card dd .app-status-badge { margin-left: $ui-space-8; }
.insurance-client-modal__actions { display: flex; align-items: center; gap: $ui-space-8; flex-wrap: wrap; margin: $ui-space-16 0; }
.insurance-client-modal__action-error { color: $ui-red; margin: $ui-space-8 0; }
.insurance-client-modal__attempts { margin-top: $ui-space-16; padding-top: $ui-space-16; border-top: 1px solid $ui-line; }
.insurance-client-modal__attempts h4 { margin: 0 0 $ui-space-8; color: $ui-ink; }
.insurance-client-modal__attempt { display: flex; justify-content: space-between; gap: $ui-space-8; padding: $ui-space-8 0; font-size: $ui-font-size-metadata; flex-wrap: wrap; }
.insurance-client-modal__footer { display: flex; justify-content: flex-end; padding: $ui-space-16 $ui-space-24; border-top: 1px solid $ui-line; }
@media (max-width: 700px) { .insurance-client-modal__summary { grid-template-columns: repeat(2, minmax(0, 1fr)); } .insurance-client-modal__cards { grid-template-columns: 1fr; } }
@media (max-width: 500px) { .insurance-client-modal__body { padding: $ui-space-16; } .insurance-client-modal__summary { grid-template-columns: 1fr; } }
</style>
